import React from "react";
import PropTypes from "prop-types";
import { animated } from "react-spring";
import { defaultProps } from "../../constants";
import { useSlider } from "../../hooks";
import Dots from "../Dots";
import Slide from "../Slide";
import SlideIndicator from "../SlideIndicator";
import {
  Overlay as StyledOverlay,
  SlideOverlay as StyledSlideOverlay,
  Slider as StyledSlider,
} from "./Slider.css";

const AnimatedOverlay = animated(StyledOverlay);
const AnimatedSlider = animated(StyledSlider);

export default function Slider({
  height,
  language,
  initialSlide,
  slides,
  slideOverlay,
  slideIndicatorTimeout,
  activeDotColor,
  dotColor,
  carouselWidth,
  hideSlideIndicator,
}) {
  let [zooming, scale, currentSlide, bind, x, onScale, updateSlideAnimate] =
    useSlider({
      initialSlide,
      slides,
      carouselWidth,
    });
  return (
    <>
      <div style={{ height: height, position: "relative" }}>
        {zooming && (
          <AnimatedOverlay
            style={{
              backgroundColor: scale
                .interpolate({ range: [1, 2, 10], output: [0, 0.7, 0.7] })
                .interpolate((opacity) => `rgba(0, 0, 0, ${opacity})`),
            }}
          />
        )}

        <StyledSlideOverlay inFront={!zooming}>
          {slideOverlay}
          {!hideSlideIndicator && (
            <SlideIndicator
              slideIndicatorTimeout={slideIndicatorTimeout}
              currentSlide={currentSlide}
              totalSlides={slides.length}
            />
          )}
        </StyledSlideOverlay>

        <AnimatedSlider
          isZooming={zooming}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...bind()}
          style={{
            transform: x.interpolate((slideX) => `translateX(${slideX}px`),
          }}
        >
          {slides.map((slide, idx) => (
            // eslint-disable-next-line react/no-array-index-key
            <Slide onScale={onScale} key={idx} carouselWidth={carouselWidth}>
              {slide}
            </Slide>
          ))}
        </AnimatedSlider>

        {slides.length > 1 && (
          <Dots
            totalSlides={slides.length}
            currentSlide={currentSlide}
            centerDots={slides.length < 6 ? slides.length : undefined}
            dotColor={dotColor}
            activeDotColor={activeDotColor}
          />
        )}
      </div>
      {slides?.length > 1 && (
        <div
          className="disable-scroll-bar"
          style={{
            display: "flex",
            overflowX: "scroll",
            flexDirection: (language === "arabic" && "row-reverse") || "row",
            margin: 10,
          }}
        >
          {slides.map((slide, index) => (
            <img
              key={`Thumb ${index}`}
              style={{
                border: currentSlide === index && "3px solid #333",
                padding: (currentSlide !== index && 5) || 3,
                objectFit: "cover",
              }}
              src={slide.props.children.props.src}
              srcSet={slide.props.children.props.srcSet}
              onClick={() => {
                updateSlideAnimate(index);
              }}
              height={60}
              width={60}
            />
          ))}
        </div>
      )}
    </>
  );
}

Slider.propTypes = {
  /** Index of the slide to be rendered by default */
  initialSlide: PropTypes.number,
  /** List of slides to render */
  slides: PropTypes.arrayOf(PropTypes.node).isRequired,
  /** Maximum zoom level */
  maxScale: PropTypes.number,
  /** Minimum zoom level */
  minScale: PropTypes.number,
  /** Content to overlay on the slider */
  slideOverlay: PropTypes.node,
  /** Time in ms until the slide indicator fades out. Set to `null` to disable this behavior. */
  slideIndicatorTimeout: PropTypes.number,
  /** Pagination dot color for the active slide */
  activeDotColor: PropTypes.string,
  /** Pagination dot color for all other slides */
  dotColor: PropTypes.string,
};

Slider.defaultProps = {
  initialSlide: defaultProps.initialSlide,
  maxScale: defaultProps.maxScale,
  minScale: defaultProps.minScale,
  slideOverlay: null,
  slideIndicatorTimeout: defaultProps.slideIndicatorTimeout,
  activeDotColor: defaultProps.activeDotColor,
  dotColor: defaultProps.dotColor,
};
